import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, EMPTY, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { NotificationService } from '../services/notification.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isHandlingLogout = false; // Flag to prevent duplicate logout handling
  
  constructor(
    private authService: AuthService,
    private router: Router,
    private notificationService: NotificationService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.authService.getToken();
    
    if (token) {
      // Clone the request and add the authorization header
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
    }
    
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse): Observable<HttpEvent<any>> => {
        // Handle authentication errors (401 Unauthorized - Session Expired)
        if (error.status === 401) {
          // Only handle logout once to prevent duplicate redirects
          if (!this.isHandlingLogout) {
            this.isHandlingLogout = true;
            
            // Clear all authentication data immediately
            this.authService.logout();
            
            // Redirect to login page immediately without showing error messages
            // Use replaceUrl to prevent back navigation
            this.router.navigate(['/'], { replaceUrl: true }).then(() => {
              // Reset flag after navigation completes
              setTimeout(() => {
                this.isHandlingLogout = false;
              }, 1000);
            }).catch(() => {
              // If navigation fails, reset flag anyway
              setTimeout(() => {
                this.isHandlingLogout = false;
              }, 1000);
            });
            
            // Return empty observable to prevent error propagation
            // This prevents any error handlers in components from showing error messages
            // Return a completed observable that matches HttpEvent type
            return new Observable<HttpEvent<any>>(observer => {
              observer.complete();
            });
          }
        }
        
        // Handle other HTTP errors (403 Forbidden - Access Denied)
        if (error.status === 403) {
          // Only handle logout once to prevent duplicate redirects
          if (!this.isHandlingLogout) {
            this.isHandlingLogout = true;
            
            // Clear all authentication data immediately
            this.authService.logout();
            
            // Redirect to login page immediately without showing error messages
            this.router.navigate(['/'], { replaceUrl: true }).then(() => {
              // Reset flag after navigation completes
              setTimeout(() => {
                this.isHandlingLogout = false;
              }, 1000);
            }).catch(() => {
              // If navigation fails, reset flag anyway
              setTimeout(() => {
                this.isHandlingLogout = false;
              }, 1000);
            });
            
            // Return empty observable to prevent error propagation
            // Return a completed observable that matches HttpEvent type
            return new Observable<HttpEvent<any>>(observer => {
              observer.complete();
            });
          }
        }
        
        // Handle server errors that might indicate authentication issues
        if (error.status === 500 && error.error && error.error.message) {
          const errorMessage = error.error.message.toLowerCase();
          if (errorMessage.includes('token') || errorMessage.includes('unauthorized') || errorMessage.includes('authentication')) {
            // Only handle logout once to prevent duplicate redirects
            if (!this.isHandlingLogout) {
              this.isHandlingLogout = true;
              
              // Clear all authentication data immediately
              this.authService.logout();
              
              // Redirect to login page immediately without showing error messages
              this.router.navigate(['/'], { replaceUrl: true }).then(() => {
                // Reset flag after navigation completes
                setTimeout(() => {
                  this.isHandlingLogout = false;
                }, 1000);
              }).catch(() => {
                // If navigation fails, reset flag anyway
                setTimeout(() => {
                  this.isHandlingLogout = false;
                }, 1000);
              });
              
              // Return empty observable to prevent error propagation
              return new Observable(observer => {
                observer.complete();
              });
            }
          }
        }
        
        // For non-authentication errors, propagate the error normally
        return throwError(error);
      })
    );
  }
}
